/**
 * Trade lanes (plan §5 R4; spec §4.3): chains of lane rings between major stations and bodies in claimed systems.
 * Only systems whose owner is a lane faction, and whose archetype or anchor qualifies, get lanes.
 */
import { nextFloat, type Rng } from '../../core/rng'
import { intRange } from '../seed'
import type { AnchorId, Archetype, RegionId } from '../types'
import type { ReachSystemsConfig } from './config'
import { add, distance, normalize, quatLookNegZ, scale, segmentDistance, segmentShellGap, sub } from './frame'
import type { BodySite, FactionKey, LaneEnd, LaneRingSite, LaneSite, StarSite, StationSite, Vec3T } from './types'

export interface LaneInput {
  systemId: string
  region: RegionId
  archetype: Archetype
  anchor: AnchorId | null
  owner: FactionKey | null
  star: StarSite
  bodies: readonly BodySite[]
  stations: readonly StationSite[]
  cfg: ReachSystemsConfig
}

export interface LaneBuild {
  lanes: LaneSite[]
  rings: LaneRingSite[]
}

/** Whether the system gets lanes at all: a lane-faction owner, and a lane archetype or anchor. */
export function qualifiesForLanes(input: Pick<LaneInput, 'owner' | 'archetype' | 'anchor' | 'cfg'>): boolean {
  const l = input.cfg.lanes
  if (input.owner === null || !l.factions.includes(input.owner)) return false
  return l.archetypes.includes(input.archetype) || (input.anchor !== null && l.anchors.includes(input.anchor))
}

/** Bodies from the largest down (ties keep their order). */
export function bodiesBySize(bodies: readonly BodySite[]): BodySite[] {
  return bodies.map((b, i) => ({ b, i })).sort((x, y) => y.b.radiusM - x.b.radiusM || x.i - y.i).map((x) => x.b)
}

interface Pair {
  from: LaneEnd
  to: LaneEnd
  length: number
  order: number
  dir: Vec3T
  /** The ring span: endClearanceM in from each end. */
  a: Vec3T
  b: Vec3T
}

function stationEnd(s: StationSite): LaneEnd {
  return { kind: 'station', id: s.id, position: s.position }
}

/** A body end sits on the body's shell, on the side facing the other end. */
function bodyEnd(b: BodySite, toward: Vec3T): LaneEnd {
  return { kind: 'body', id: b.id, position: add(b.position, scale(normalize(sub(toward, b.position)), b.shellM)) }
}

/**
 * Candidates are station pairs, and each station with the largest body it can reach (the largest body, or the next
 * largest when that lane would fail), at least `minLengthM` long, shortest first. A candidate is valid when its ring
 * span (`endClearanceM` in from each end) keeps `shellClearanceM` from every shell and clears the star. Candidates
 * are taken while fewer than `maxLanes` exist, if their body end serves no other lane (so lanes never fan out in
 * parallel to one body). Rings are evenly spaced over the span; each is down with `downChance[region]`.
 */
export function placeLanes(input: LaneInput, rng: Rng): LaneBuild {
  const { cfg, stations, bodies, star } = input
  const l = cfg.lanes
  const owner = input.owner
  if (owner === null || !qualifiesForLanes(input)) return { lanes: [], rings: [] }
  const starClear = cfg.pois.starClearanceFactor * star.radiusM
  const pairs: Pair[] = []
  const valid = (from: LaneEnd, to: LaneEnd): Pair | null => {
    const length = distance(from.position, to.position)
    if (length < l.minLengthM || length <= 2 * l.endClearanceM) return null
    const dir = normalize(sub(to.position, from.position))
    const a = add(from.position, scale(dir, l.endClearanceM))
    const b = sub(to.position, scale(dir, l.endClearanceM))
    if (segmentShellGap(a, b, bodies) < l.shellClearanceM) return null
    if (segmentDistance([0, 0, 0], a, b) < starClear) return null
    return { from, to, length, order: pairs.length, dir, a, b }
  }
  const bySize = bodiesBySize(bodies)
  for (let i = 0; i < stations.length; i++) {
    for (let j = i + 1; j < stations.length; j++) {
      const p = valid(stationEnd(stations[i]), stationEnd(stations[j]))
      if (p !== null) pairs.push(p)
    }
    for (const body of bySize) {
      const p = valid(stationEnd(stations[i]), bodyEnd(body, stations[i].position))
      if (p === null) continue
      pairs.push(p)
      break
    }
  }
  pairs.sort((x, y) => x.length - y.length || x.order - y.order)
  const lanes: LaneSite[] = []
  const rings: LaneRingSite[] = []
  const usedBodies = new Set<string>()
  for (const p of pairs) {
    if (lanes.length >= l.maxLanes) break
    if (p.to.kind === 'body' && usedBodies.has(p.to.id)) continue
    if (p.to.kind === 'body') usedBodies.add(p.to.id)
    const laneIndex = lanes.length
    const laneId = `${input.systemId}-lane${laneIndex}`
    const n = intRange(rng, l.ringsPerLane[0], l.ringsPerLane[1])
    const ringIndices: number[] = []
    const rotation = quatLookNegZ(p.dir)
    const span = sub(p.b, p.a)
    for (let k = 0; k < n; k++) {
      const index = rings.length
      ringIndices.push(index)
      rings.push({
        id: `${laneId}-r${k}`,
        index,
        laneIndex,
        order: k,
        position: add(p.a, scale(span, k / (n - 1))),
        rotation,
        apertureM: l.apertureM,
        initiallyUp: nextFloat(rng) >= l.downChance[input.region],
        assetId: 'reach.lane_ring',
      })
    }
    lanes.push({ id: laneId, index: laneIndex, faction: owner, from: p.from, to: p.to, dir: p.dir, ringIndices })
  }
  return { lanes, rings }
}
